import React from 'react'
import classNames from 'classnames'
import { formatRelative, formatDate } from '@deal/date-utils'
import { Image, Button } from '@deal/components'
import { Link } from 'react-router-dom'
import truncate from 'lodash/truncate'
import Grid, { GridItem } from '#src/app/components/Grid'
import { getDepartmentExpertHeadline, getExpertImageAltText } from '#src/app/services/expert'
import useBreakpoint from '#src/app/hooks/useBreakpoint'
import numeral from 'numeral'
import SliderWithScrollLock, {
  SlickSlider,
  CustomArrowProps
} from '#src/app/components/SliderWithScrollLock'
import useViewportSize from '#src/app/hooks/useViewportSize'
import { FeaturedReviewsComponentProps } from '..'
import Star from './star.svg'
import LeftArrow from './left-arrow.svg'
import RightArrow from './right-arrow.svg'
import styles from './styles.css'
import ExpertProfileLink from '../../ExpertProfileLink'

const PrevArrow: React.FC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div className={styles.prevArrow} onClick={onClick}>
      <LeftArrow className={styles.arrowIcon} />
    </div>
  )
}

const NextArrow: React.FC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div className={styles.nextArrow} onClick={onClick}>
      <RightArrow className={styles.arrowIcon} />
    </div>
  )
}

interface ReviewDotsProps {
  dots: React.ReactNode[]
}

const ReviewDots: React.FC<ReviewDotsProps> = ({ dots }) => (
  <div>
    <div className={styles.dotsAndArrows}>
      <div className={styles.dotContainer}>
        <ul className={styles.dots}>{dots}</ul>
      </div>
    </div>
  </div>
)

const Reviews: React.FC<FeaturedReviewsComponentProps> = ({
  className,
  header,
  subHeader,
  disableLinking = false,
  showMoreReviewsButton = true,
  featuredReviews,
  actions
}) => {
  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanMd = useBreakpoint('md')

  let slidesToShow = 1.5
  if (isLargerThanMd) {
    slidesToShow = 3
  }

  const sliderRef: React.RefObject<SlickSlider> = React.createRef()
  const sliderContRef: React.RefObject<HTMLDivElement> = React.createRef()
  const headerTruncateThreshold = isLargerThanSm ? 58 : 38
  const bodyTruncateTreshold = isLargerThanSm ? 230 : 150

  useViewportSize(() => {
    if (sliderContRef.current) {
      const sliderContainer = sliderContRef.current.getElementsByClassName('slick-track')[0]
      if (sliderContainer instanceof HTMLDivElement) {
        const cardDiv = sliderContainer.getElementsByClassName('slick-slide')[0]
        if (cardDiv instanceof HTMLDivElement) {
          const width = cardDiv.offsetWidth
          sliderContainer.style.marginLeft = isLargerThanMd
            ? '0'
            : `${(document.body.clientWidth - (width - 15)) / 2}px`
        }
      }
    }
  })

  // Filter out review if the review expert does not have a profile photo
  let filteredReviews = featuredReviews.filter(
    review => !!review.expert.expertProfile?.profilePhoto
  )

  // We limit the number of reviews for mobile to prevent
  //  the dots from exceeding the page width
  if (!isLargerThanMd) {
    filteredReviews = filteredReviews.slice(0, 7)
  }

  return (
    <div className={classNames(styles.wrapper, className)}>
      <Grid>
        <GridItem col={2}>
          {header && <div className={styles.header}>{header}</div>}
          {subHeader && <div className={styles.subHeader}>{subHeader}</div>}
        </GridItem>
      </Grid>

      <Grid className={styles.reviewCarousel}>
        <GridItem col={2}>
          <div ref={sliderContRef}>
            <SliderWithScrollLock
              sliderRef={sliderRef}
              slidesToShow={slidesToShow}
              slidesToScroll={1}
              dots
              infinite={false}
              arrows={isLargerThanMd}
              prevArrow={isLargerThanMd ? <PrevArrow /> : undefined}
              nextArrow={isLargerThanMd ? <NextArrow /> : undefined}
              appendDots={dots => (
                <ReviewDots
                  // @ts-ignore This is passing an array and is incorrectly typed
                  dots={dots}
                />
              )}
              className={styles.carousel}
            >
              {filteredReviews.map(review => {
                const expertHeadline = getDepartmentExpertHeadline({
                  departmentType: review.expert.department.type
                })
                const expertImageAltText = getExpertImageAltText(review.expert)

                return (
                  <div key={review.id}>
                    <div className={styles.reviewCard}>
                      <div className={styles.cardHeader}>
                        {disableLinking ? (
                          <Image
                            src={review.expert.expertProfile!.profilePhoto!.url}
                            sizes="(min-width: 768px) 200px, 150px"
                            className={styles.cardImage}
                            htmlAttributes={{
                              alt: expertImageAltText
                            }}
                          />
                        ) : (
                          <ExpertProfileLink expert={review.expert}>
                            <Image
                              src={review.expert.expertProfile!.profilePhoto!.url}
                              sizes="(min-width: 768px) 200px, 150px"
                              className={styles.cardImage}
                              htmlAttributes={{
                                alt: expertImageAltText
                              }}
                            />
                          </ExpertProfileLink>
                        )}

                        <div className={styles.cardHeaderContent}>
                          <div className={styles.cardHeaderNameContainer}>
                            {disableLinking ? (
                              <>
                                <div className={styles.cardHeaderExpert}>
                                  {review.expert.shortName}
                                </div>

                                <div className={styles.cardHeaderRatingContainer}>
                                  {numeral(review.rating).format('0.0')}
                                  <Star className={styles.cardHeaderRatingStar} />
                                </div>
                              </>
                            ) : (
                              <>
                                <ExpertProfileLink expert={review.expert}>
                                  <div className={styles.cardHeaderExpert}>
                                    {review.expert.displayName}
                                  </div>
                                </ExpertProfileLink>
                                <ExpertProfileLink expert={review.expert}>
                                  <div className={styles.cardHeaderRatingContainer}>
                                    {numeral(review.rating).format('0.0')}
                                    <Star className={styles.cardHeaderRatingStar} />
                                  </div>
                                </ExpertProfileLink>
                              </>
                            )}
                          </div>
                          <div className={styles.cardHeaderExpertTitle}>{expertHeadline}</div>
                        </div>
                      </div>
                      <div className={styles.cardContent}>
                        {review.headline && (
                          <div className={styles.reviewHeadline}>
                            {truncate(review.headline, {
                              length: headerTruncateThreshold
                            })}
                          </div>
                        )}
                        {review.comments && (
                          <div className={styles.reviewBody}>
                            <span>
                              "
                              {truncate(review.comments, {
                                length: bodyTruncateTreshold
                              })}
                              "&nbsp;
                            </span>
                            {review.comments.length > bodyTruncateTreshold && !disableLinking && (
                              <ExpertProfileLink
                                expert={review.expert}
                                reviewId={review.id}
                              >
                                <span className={styles.reviewExpand}>expand</span>
                              </ExpertProfileLink>
                            )}

                            {review.consumerName && (
                              <div className={styles.consumerName}>
                                - {review.consumerName},{' '}
                                <time
                                  dateTime={review.reviewedAt}
                                  title={formatDate(review.reviewedAt, { format: 'long' })}
                                >
                                  {formatRelative(review.reviewedAt, {
                                    specificity: 'general'
                                  })}
                                </time>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </SliderWithScrollLock>
          </div>
        </GridItem>
      </Grid>

      {(showMoreReviewsButton || actions) && (
        <Grid>
          <GridItem col={2} className={styles.actions}>
            {actions ? (
              <>{actions}</>
            ) : (
              <Link to="/reviews#reviews">
                <Button variant="important-ghost">More reviews</Button>
              </Link>
            )}
          </GridItem>
        </Grid>
      )}
    </div>
  )
}

export default Reviews
